"use client";

import { FC, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface Service {
  id: number;
  name: string;
  slug: string;
  description: string;
  image: string;
  icon?: string;
}

const ServicesSection: FC<{ initialData?: Service[] }> = ({ initialData }) => {
  const pathname = usePathname();
  const isHome = pathname === "/" || pathname === "/home";
  const isServicesPage = pathname === "/services";

  const homeLimit = 3;
  const servicesPageLimit = 6;

  const [services, setServices] = useState<Service[]>(initialData || []);
  const [visibleCount, setVisibleCount] = useState(
    isHome ? homeLimit : servicesPageLimit,
  );
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (services.length > 0) return;

    const fetchServices = async () => {
      try {
        const res = await fetch("https://portal.renvixx.com/api/services");
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const contentType = res.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Received non-JSON response from server");
        }
        const data = await res.json();
        if (data.success && data.data) {
          setServices(data.data);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [services]);

  /* =============================
     SORT SERVICES BY ID (ASC)
     ============================= */
  const sortedServices = [...services].sort((a, b) => a.id - b.id);

  const displayedServices = sortedServices.slice(0, visibleCount);

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 3, sortedServices.length));
  };

  const createSlug = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  if (loading) {
    return (
      <section className="py-20 text-center">
        <p>Loading services...</p>
      </section>
    );
  }

  return (
    <section className="bg-white text-[#2a6380] py-20 font-outfit">
      {/* ===== Heading ===== */}
      <div className="text-center mb-14 px-4">
        <p className="text-sm text-gray-500">Services</p>
        <h2 className="text-3xl sm:text-4xl font-bold mt-2">
          Services That Fit <br />
          <span className="text-[#33c1c7]">Your Needs</span>
        </h2>
      </div>

      {/* ===== Service Cards ===== */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-6 lg:px-8">
        {displayedServices.map((service) => {
          const slug = service.slug || createSlug(service.name);

          return (
            <div
              key={service.id}
              className="rounded-2xl border-2 border-[#33c1c761] overflow-hidden shadow-md bg-white hover:shadow-lg transition-shadow"
            >
              <Link
                href={`/services/${slug}`}
                className="block w-full h-56 sm:h-60 lg:h-64 overflow-hidden"
              >
                <Image
                  src={service.image}
                  alt={service.name}
                  width={600}
                  height={400}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </Link>

              <div className="px-6 pb-6 flex flex-col">
                {service.icon && (
                  <div className="flex items-center justify-center -mt-6 z-10 bg-[#2a6380] rounded-full w-12 h-12 mb-4">
                    <Image
                      src={service.icon}
                      alt={service.name}
                      width={24}
                      height={24}
                    />
                  </div>
                )}

                <Link href={`/services/${slug}`}>
                  <h3 className="text-lg font-semibold mb-2">{service.name}</h3>
                </Link>

                <p
                  className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: service.description }}
                />

                <div className="mt-auto">
                  <Link
                    href={`/services/${slug}`}
                    className="relative overflow-hidden inline-block border-2 border-[#2a6380] bg-[#2a6380] text-white text-[12px] font-semibold px-5 py-2 rounded-md transition-all duration-500 ease-in-out group"
                  >
                    <span className="relative z-10 transition-colors duration-500 group-hover:text-[#2a6380]">
                      Learn More
                    </span>
                    <span className="absolute inset-0 bg-white scale-[-1] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Button ===== */}
      {((isHome && sortedServices.length > homeLimit) ||
        (isServicesPage && visibleCount < sortedServices.length)) && (
        <div className="max-w-[200px] mx-auto mt-12 text-center">
          {isHome ? (
            <Link
              href="/services"
              className="relative overflow-hidden inline-block border-2 border-[#2a6380] bg-[#2a6380] text-white text-sm font-semibold px-8 py-3 rounded transition-all duration-500 ease-in-out group"
            >
              <span className="relative z-10 transition-colors duration-500 group-hover:text-[#2a6380]">
                Learn More Services
              </span>
              <span className="absolute inset-0 bg-white scale-[-1] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
            </Link>
          ) : (
            <button
              onClick={handleLoadMore}
              className="relative overflow-hidden inline-block border-2 border-[#2a6380] bg-[#2a6380] text-white text-sm font-semibold px-8 py-3 rounded transition-all duration-500 ease-in-out group"
            >
              <span className="relative z-10 transition-colors duration-500 group-hover:text-[#2a6380]">
                Load More Services
              </span>
              <span className="absolute inset-0 bg-white scale-[-1] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out"></span>
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default ServicesSection;
